const API_BASE_URL = '/api/v1';

export interface Email {
  id: string;
  company_id?: string;
  account_id?: string;
  direction: 'inbound' | 'outbound';
  from_email: string;
  from_name?: string;
  to_emails: string[];
  cc_emails?: string[];
  bcc_emails?: string[];
  subject: string;
  body_text: string;
  body_html?: string;
  status:
    | 'draft'
    | 'queued'
    | 'sent'
    | 'delivered'
    | 'opened'
    | 'clicked'
    | 'bounced'
    | 'failed'
    | 'deleted'
    | 'archived';
  starred?: boolean;
  created_at: string;
  sent_at?: string;
  delivered_at?: string;
  opened_at?: string;
  first_clicked_at?: string;
  open_count?: number;
  click_count?: number;
  related_to_type?: string;
  related_to_id?: string;
  attachments?: any[];
  thread_id?: string;
  in_reply_to?: string;
  ai_generated?: boolean;
  ai_model?: string;
  template_id?: string;
  template_name?: string;
}

export interface EmailsResponse {
  success: boolean;
  data: Email[];
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  error?: string;
  // Backend may also return additional fields like `filters` for search
  [key: string]: any;
}

export interface EmailResponse {
  success: boolean;
  data?: Email;
  error?: string;
  message?: string;
}

export interface EmailStats {
  total: number;
  inbox: number;
  sent: number;
  drafts: number;
  unread: number;
  openRate: number;
}

// Filters for server-side email search
export interface EmailSearchFilters {
  query?: string;
  from?: string;
  to?: string;
  subject?: string;
  hasAttachments?: boolean | null;
  isUnread?: boolean | null;
  dateFrom?: string;
  dateTo?: string;
  folder?: string;
  page?: number;
  limit?: number;
}

class EmailsService {
  private baseUrl: string;

  constructor() {
    this.baseUrl = API_BASE_URL;
  }

  async getAll(page = 1, limit = 50, category = 'all', search = ''): Promise<EmailsResponse> {
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        limit: limit.toString(),
        category,
        ...(search && { search })
      });

      const response = await fetch(`${this.baseUrl}/emails?${params}`, {
        credentials: 'include'
      });
      const data = await response.json();

      return data;
    } catch (error) {
      console.error('Error fetching emails:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Failed to fetch emails'
      };
    }
  }

  async getById(id: string): Promise<EmailResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/emails/${id}`, {
        credentials: 'include'
      });
      const data = await response.json();

      return data;
    } catch (error) {
      console.error('Error fetching email:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch email'
      };
    }
  }

  async send(emailData: {
    to_emails: string | string[];
    cc_emails?: string[];
    bcc_emails?: string[];
    subject: string;
    body_text: string;
    body_html?: string;
    related_to_type?: string;
    related_to_id?: string;
    attachments?: any[];
  }): Promise<EmailResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/emails`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(emailData)
      });

      const data = await response.json();

      return data;
    } catch (error) {
      console.error('Error sending email:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send email'
      };
    }
  }

  async update(id: string, updates: Partial<Email>): Promise<EmailResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/emails/${id}`, {
        method: 'PUT',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updates)
      });

      const data = await response.json();

      return data;
    } catch (error) {
      console.error('Error updating email:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to update email'
      };
    }
  }

  async delete(id: string): Promise<EmailResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/emails/${id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await response.json();

      return data;
    } catch (error) {
      console.error('Error deleting email:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete email'
      };
    }
  }

  async toggleRead(id: string): Promise<EmailResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/emails/${id}/read`, {
        method: 'PATCH',
        credentials: 'include'
      });

      const data = await response.json();

      return data;
    } catch (error) {
      console.error('Error toggling read status:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to toggle read status'
      };
    }
  }

  async restore(id: string): Promise<EmailResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/emails/${id}/restore`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();

      return data;
    } catch (error) {
      console.error('Error restoring email:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to restore email'
      };
    }
  }

  async deletePermanently(id: string): Promise<EmailResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/emails/${id}/permanent`, {
        method: 'DELETE',
        credentials: 'include'
      });

      const data = await response.json();

      return data;
    } catch (error) {
      console.error('Error permanently deleting email:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to permanently delete email'
      };
    }
  }

  async emptyTrash(): Promise<EmailResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/emails/trash/empty`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();

      return data;
    } catch (error) {
      console.error('Error emptying trash:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to empty trash'
      };
    }
  }

  async getTrash(accountId: string): Promise<EmailsResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/email-trash/${accountId}`, {
        credentials: 'include'
      });

      if (!response.ok) {
        throw new Error('Failed to fetch trash');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching trash emails:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Failed to fetch trash emails'
      };
    }
  }

  async restoreFromTrash(emailId: string, accountId: string): Promise<EmailResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/email-trash/${emailId}/restore`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ accountId }),
        credentials: 'include'
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error restoring email:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to restore email'
      };
    }
  }

  async permanentDeleteEmail(emailId: string, accountId: string): Promise<EmailResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/email-trash/${emailId}/permanent`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ accountId }),
        credentials: 'include'
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error permanently deleting email:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to permanently delete email'
      };
    }
  }

  async emptyEmailTrash(accountId: string): Promise<EmailResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/email-trash/${accountId}/empty`, {
        method: 'POST',
        credentials: 'include'
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error emptying trash:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to empty trash'
      };
    }
  }

  async getTrashStats(): Promise<{ success: boolean; data?: { totalInTrash: number; willBeDeletedSoon: number; cutoffDate: string }; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/emails/trash/stats`, {
        credentials: 'include'
      });
      const data = await response.json();

      return data;
    } catch (error) {
      console.error('Error fetching trash stats:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch trash stats'
      };
    }
  }

  async getStats(): Promise<{ success: boolean; data?: EmailStats; error?: string }> {
    try {
      const response = await fetch(`${this.baseUrl}/emails/stats/overview`, {
        credentials: 'include'
      });
      const data = await response.json();

      return data;
    } catch (error) {
      console.error('Error fetching email stats:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to fetch email stats'
      };
    }
  }

  async search(filters: EmailSearchFilters): Promise<EmailsResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/emails/search`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          query: filters.query || '',
          from: filters.from || null,
          to: filters.to || null,
          subject: filters.subject || null,
          hasAttachments: filters.hasAttachments ?? null,
          isUnread: filters.isUnread ?? null,
          dateFrom: filters.dateFrom || null,
          dateTo: filters.dateTo || null,
          folder: filters.folder || null,
          page: filters.page || 1,
          limit: filters.limit || 50
        })
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error searching emails:', error);
      return {
        success: false,
        data: [],
        error: error instanceof Error ? error.message : 'Failed to search emails'
      };
    }
  }

  async saveDraft(draftData: {
    account_id: string;
    to?: string[];
    cc?: string[];
    bcc?: string[];
    subject?: string;
    text?: string;
    html?: string;
    attachments?: any[];
  }): Promise<EmailResponse> {
    try {
      const response = await fetch(`${this.baseUrl}/emails/draft`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(draftData)
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error saving draft:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Fehler beim Speichern des Entwurfs'
      };
    }
  }

  async getSearchSuggestions(
    query: string
  ): Promise<{ success: boolean; data: Array<{ type: string; value: string; label: string }> }> {
    try {
      if (!query || query.length < 2) {
        return { success: true, data: [] };
      }

      const response = await fetch(
        `${this.baseUrl}/emails/search/suggestions?query=${encodeURIComponent(query)}`,
        { credentials: 'include' }
      );
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching search suggestions:', error);
      return { success: true, data: [] };
    }
  }

  // Helper function to format email for display
  formatEmailForDisplay(email: Email): {
    id: string;
    from: string;
    to: string;
    subject: string;
    preview: string;
    body: string;
    date: string;
    time: string;
    read: boolean;
    starred: boolean;
    important: boolean;
    attachments: number;
    category: 'inbox' | 'sent' | 'drafts' | 'trash' | 'archived';
    status?: 'delivered' | 'read' | 'pending';
  } {
    const createdDate = new Date(email.created_at);
    const isRead = !!email.opened_at || email.direction === 'outbound';

    return {
      id: email.id,
      from: email.from_name || email.from_email,
      to: email.to_emails.join(', '),
      subject: email.subject || '(Kein Betreff)',
      preview: email.body_text?.substring(0, 150) || '',
      body: email.body_html || email.body_text || '',
      date: createdDate.toISOString().split('T')[0],
      time: createdDate.toLocaleTimeString('de-DE', {
        hour: '2-digit',
        minute: '2-digit'
      }),
      read: isRead,
      starred: false, // TODO: Implement starring feature
      important: email.status === 'bounced' || email.status === 'failed',
      attachments: email.attachments?.length || 0,
      category: this.getCategory(email),
      status: this.getStatus(email)
    };
  }

  private getCategory(email: Email): 'inbox' | 'sent' | 'drafts' | 'trash' | 'archived' {
    if (email.status === 'deleted') return 'trash';
    if (email.status === 'archived') return 'archived';
    if (email.status === 'draft') return 'drafts';
    if (email.direction === 'inbound') return 'inbox';
    return 'sent';
  }

  private getStatus(email: Email): 'delivered' | 'read' | 'pending' | undefined {
    if (email.opened_at) return 'read';
    if (email.delivered_at) return 'delivered';
    if (email.status === 'sent' || email.status === 'queued') return 'pending';
    return undefined;
  }
}

const emailsService = new EmailsService();

export default emailsService;
